import type { PersistedQuestionnaireAnswerOp } from '@seen/core/models/compositionNote'
import { create } from 'zustand'
import { combine } from 'zustand/middleware'

/* One entry of a `proposeQuestionnaireAnswers` call, in the shape the staged
 * record persists — so the care plan surface renders a streamed atom through
 * exactly the same path as a persisted one. */
export type StreamingQuestionnaireAnswer =
  PersistedQuestionnaireAnswerOp['answers'][number]

export interface StreamingQuestionnaireSuggestion {
  /* The in-flight tool call these answers belong to. The care plan surface
   * drops the entry once the call's persisted record carries the same id, so a
   * streamed atom can never outlive — or resurrect after — its record. */
  toolCallId: string
  questionnaireUrl: string
  /* The answers whose JSON has closed so far, in the order the model emitted
   * them. Always a PREFIX of what the completed call persists. */
  answers: StreamingQuestionnaireAnswer[]
}

/**
 * The still-streaming `proposeQuestionnaireAnswers` entries, held in memory
 * only. Nothing here is persisted: a tool call is atomic, so a 28-answer
 * proposal otherwise shows the clinician nothing for the minute it takes to
 * generate and then every card at once. The chat hook appends each entry as its
 * JSON closes; the care plan surface paints those entries until the completing
 * call's staging `Basic` write lands, which is the only authoritative record.
 *
 * Mirrors how `editEditor` already paints ops mid-stream through the editor
 * registry — the registry's decoration store is ProseMirror-only, so a
 * questionnaire needs its own in-memory hold of the same shape.
 */
export const useStreamingQuestionnaireSuggestionStore = create(
  combine({ entries: [] as StreamingQuestionnaireSuggestion[] }, (set) => ({
    /* Append newly-closed answers to a call's entry (creating it on the first
     * ones). `questionnaireUrl` is re-read every time so a url that finished
     * streaming after its answers still routes them to the right block. */
    stageStreamingAnswers: ({
      toolCallId,
      questionnaireUrl,
      answers,
    }: {
      toolCallId: string
      questionnaireUrl: string
      answers: StreamingQuestionnaireAnswer[]
    }) =>
      set((state) => {
        if (answers.length === 0) {
          return state
        }
        const existing = state.entries.find((e) => e.toolCallId === toolCallId)
        if (!existing) {
          return {
            entries: [
              ...state.entries,
              { toolCallId, questionnaireUrl, answers },
            ],
          }
        }
        return {
          entries: state.entries.map((entry) =>
            entry.toolCallId === toolCallId
              ? {
                  toolCallId,
                  questionnaireUrl,
                  answers: [...entry.answers, ...answers],
                }
              : entry
          ),
        }
      }),
    /* Drop one call's streamed answers — when its persisted record has taken
     * over, and when the run errored or was aborted so the completing call that
     * would have superseded them never arrives. */
    clearStreamingAnswers: (toolCallId: string) =>
      set((state) =>
        state.entries.some((e) => e.toolCallId === toolCallId)
          ? {
              entries: state.entries.filter((e) => e.toolCallId !== toolCallId),
            }
          : state
      ),
  }))
)

/**
 * The latest streamed proposal for a questionnaire, or undefined when none is
 * in flight. Latest wins, matching how the staging `Basic` resolves several
 * pending records for one questionnaire.
 * @param questionnaireUrl - Canonical url of the questionnaire being rendered.
 * @returns The in-flight entry for that questionnaire, if any.
 */
export function useStreamingQuestionnaireSuggestion(
  questionnaireUrl: string
): StreamingQuestionnaireSuggestion | undefined {
  return useStreamingQuestionnaireSuggestionStore((state) => {
    for (let i = state.entries.length - 1; i >= 0; i--) {
      const entry = state.entries[i]
      if (entry?.questionnaireUrl === questionnaireUrl) {
        return entry
      }
    }
    return undefined
  })
}
